/**
 * Bank account data-access layer for the Add Payment Account modal.
 *
 * The real flow is two-step (verify→create): raw account/routing numbers go ONLY to the verify
 * step; create receives a gateway token + masked data.
 */

import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  AddBankAccountDocument,
  type AddBankAccountFailureCode,
  type AddBankAccountInput,
  EditBankAccountDocument,
  type EditBankAccountFailureCode,
  type EditBankAccountInput,
  type BankAccountType as GqlBankAccountType,
  RemoveBankAccountDocument,
  type RemoveBankAccountFailureCode,
  type VerifyBankAccountFailureCode,
  VerifyBankAccountInformationDocument,
} from '@/graphql/generated/graphql';

import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import type { BankAccountType } from '@/lib/payments/paymentAccount.types';

/** Form value (lowercase) → wire enum (uppercase). No cast — a new form value fails to compile here. */
export const BANK_ACCOUNT_TYPE_TO_GQL: Record<BankAccountType, GqlBankAccountType> = {
  checking: 'CHECKING',
  savings: 'SAVINGS',
  other: 'OTHER',
};

/** Wire enum (uppercase) → form value (lowercase). Reverse of {@link BANK_ACCOUNT_TYPE_TO_GQL}. */
const GQL_TO_BANK_ACCOUNT_TYPE: Record<GqlBankAccountType, BankAccountType> = {
  CHECKING: 'checking',
  SAVINGS: 'savings',
  OTHER: 'other',
};

/** Bank accounts have no card network — a separate shape from credit card's VerifyResult avoids a fake value. */
export interface VerifyAchResult {
  gatewayTokenId: string;
  authTransactionId: string;
}

/** Thrown when verifyBankAccountInformation returns a domain Failure variant (DECLINED | UNSUPPORTED_ACCOUNT). */
export class PaymentVerifyAchError extends Error {
  constructor(public readonly code: VerifyBankAccountFailureCode) {
    super(code);
    this.name = 'PaymentVerifyAchError';
  }
}

/**
 * Thrown when addBankAccount returns its Failure variant. Mirrors PaymentAddCardError —
 * `AddBankAccountFailureCode` has a single `RESERVED` placeholder (no domain-decline path today).
 */
export class PaymentAddBankAccountError extends Error {
  constructor(public readonly code: AddBankAccountFailureCode) {
    super(code);
    this.name = 'PaymentAddBankAccountError';
  }
}

/**
 * Thrown when editBankAccount returns its Failure variant. `EditBankAccountFailureCode` has a
 * single `NOT_FOUND` value, deliberately overloading unknown id / not-owned-by-caller / deactivated
 * account to avoid an enumeration oracle — same reasoning as credit card's PaymentEditCardError.
 */
export class PaymentEditBankAccountError extends Error {
  constructor(public readonly code: EditBankAccountFailureCode) {
    super(code);
    this.name = 'PaymentEditBankAccountError';
  }
}

/**
 * Thrown when removeBankAccount returns its Failure variant (NOT_FOUND, deliberately overloading
 * unknown id / not-owned-by-caller / already-inactive to avoid an enumeration oracle — same
 * reasoning as PaymentEditBankAccountError — or DEFAULT_ACCOUNT when the target is the caller's
 * default).
 */
export class PaymentRemoveBankAccountError extends Error {
  constructor(public readonly code: RemoveBankAccountFailureCode) {
    super(code);
    this.name = 'PaymentRemoveBankAccountError';
  }
}

export interface VerifyAchRequest {
  accountNumber: string;
  routingNumber: string;
}

export interface CreateProfileResult {
  id: string;
}

export async function requestVerifyAch(input: VerifyAchRequest): Promise<VerifyAchResult> {
  const data = await gqlRequest(VerifyBankAccountInformationDocument, {
    input: { routingNumber: input.routingNumber, accountNumber: input.accountNumber },
  });
  const result = data.verifyBankAccountInformation;
  if (result.__typename === 'VerifyBankAccountInformationFailure') {
    throw new PaymentVerifyAchError(result.code);
  }
  return { gatewayTokenId: result.gatewayTokenId, authTransactionId: result.authTransactionId };
}

export async function requestCreateBankProfile(input: AddBankAccountInput): Promise<CreateProfileResult> {
  const data = await gqlRequest(AddBankAccountDocument, { input });
  const result = data.addBankAccount;
  if (result.__typename === 'AddBankAccountFailure') {
    throw new PaymentAddBankAccountError(result.code);
  }
  return { id: result.account.id };
}

/**
 * Edits a saved bank account in place. Returns the updated account as reported by the mutation —
 * the Salesforce Flow atomically demotes any prior default server-side, but the response only
 * carries this one account, so the caller is responsible for reconciling other rows (see
 * useEditBankAccount).
 */
export async function requestEditBankAccount(id: string, input: EditBankAccountInput): Promise<BankAccountAccount> {
  const data = await gqlRequest(EditBankAccountDocument, { id, input });
  const result = data.editBankAccount;
  if (result.__typename === 'EditBankAccountFailure') {
    throw new PaymentEditBankAccountError(result.code);
  }
  return mapSavedBankAccount(result.account);
}

/** Removes (soft-deletes) a saved bank account. Rejects the caller's current default — see PaymentRemoveBankAccountError. */
export async function requestRemoveBankAccount(id: string): Promise<{ id: string }> {
  const data = await gqlRequest(RemoveBankAccountDocument, { input: { id } });
  const result = data.removeBankAccount;
  if (result.__typename === 'RemoveBankAccountFailure') {
    throw new PaymentRemoveBankAccountError(result.code);
  }
  return { id: result.account.id };
}

/** Structural shape mapSavedBankAccount actually reads — mirrors credit card's `SavedCreditCardFields`. */
export interface SavedBankAccountFields {
  id: string;
  last6: string;
  bankName?: string | null;
  accountType: GqlBankAccountType;
  nameOnAccount?: string | null;
  billingAddress?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  isDefault: boolean;
  isHSA: boolean;
  isActive: boolean;
}

export function mapSavedBankAccount(account: SavedBankAccountFields): BankAccountAccount {
  return {
    id: account.id,
    last6: account.last6,
    bankName: account.bankName ?? '',
    accountType: GQL_TO_BANK_ACCOUNT_TYPE[account.accountType],
    holderName: account.nameOnAccount ?? '',
    line1: account.billingAddress?.line1 ?? '',
    line2: account.billingAddress?.line2 ?? '',
    city: account.billingAddress?.city ?? '',
    state: account.billingAddress?.state ?? '',
    zip: account.billingAddress?.zip ?? '',
    isDefault: account.isDefault,
    isHsa: account.isHSA,
    isActive: account.isActive,
  };
}
